import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion } from "framer-motion";

const ImageCard = ({ src, alt, index, variants }) => (
  <motion.div
    variants={variants}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    custom={index}
    className="aspect-[4/3] rounded-2xl overflow-hidden shadow-sm bg-neutral-200"
  >
    <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" decoding="async" />
  </motion.div>
);

const SquareCard = ({ src, alt }) => (
  <div className="aspect-square rounded-2xl overflow-hidden bg-neutral-200">
    <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" decoding="async" />
  </div>
);

const heroImages = [
  { src: "/media/hero-tonya-garden.jpg", alt: "Tonya in her garden" },
  { src: "/media/hero-tonya-watering.jpg", alt: "Tonya watering plants" },
];

const aboutPhotos = [
  { src: "/media/about-1.jpg", alt: "Tonya portrait" },
  { src: "/media/about-2.jpg", alt: "Tonya potting plants" },
  { src: "/media/about-3.jpg", alt: "Garden close-up" },
];

const tonyaPostsSeed = [
  {
    title: "Starting My Fall Garden",
    excerpt: "What I’m planting this season and why…",
    image: "/media/blog-fall.jpg",
    category: "My Journey",
    date: "Oct 12, 2025",
  },
  {
    title: "5 Watering Mistakes (And Fixes)",
    excerpt: "Quick tips to avoid over & under-watering.",
    image: "/media/blog-watering.jpg",
    category: "Tips & Tricks",
    date: "Oct 8, 2025",
  },
  {
    title: "Zone 8 Planting Calendar",
    excerpt: "A simple guide to what to plant each month.",
    image: "/media/blog-calendar.jpg",
    category: "Seasonal Guides",
    date: "Oct 3, 2025",
  },
];

const cornerGuides = [
  { title: "Soil Basics 101", image: "/media/guide-soil.jpg" },
  { title: "Light & Placement", image: "/media/guide-light.jpg" },
  { title: "Watering Made Simple", image: "/media/guide-water.jpg" },
  { title: "Seasonal Planting", image: "/media/guide-seasonal.jpg" },
];

const gallery = [
  "/media/gallery-1.jpg",
  "/media/gallery-2.jpg",
  "/media/gallery-3.jpg",
  "/media/gallery-4.jpg",
  "/media/gallery-5.jpg",
  "/media/gallery-6.jpg",
  "/media/gallery-7.jpg",
  "/media/gallery-8.jpg",
];

const videos = [
  { src: "/media/intro-tonya.mp4", poster: "/media/video-poster.jpg", caption: "Welcome to Tonya's Blooms" },
  { src: "/media/repotting-tips.mp4", poster: "/media/video-repotting.jpg", caption: "Repotting 101" },
];

const navVariants = {
  hidden: { y: -30, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.5 } },
};

const cardVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: (i = 1) => ({ y: 0, opacity: 1, transition: { duration: 0.4, delay: i * 0.08 } }),
};

function parseDateSafe(d) {
  if (!d) return null;
  const ts = Date.parse(d);
  return Number.isNaN(ts) ? null : new Date(ts);
}

async function safeFetchJson(url, timeoutMs = 4500) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal, headers: { "Accept": "application/json" } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(t);
  }
}

function normalizePosts(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter(p => p && typeof p === "object")
    .map(p => ({
      title: String(p.title || "Untitled"),
      excerpt: String(p.excerpt || ""),
      image: String(p.image || "/media/placeholder.jpg"),
      category: String(p.category || "AI Insight"),
      date: String(p.date || new Date().toDateString()),
    }))
    .filter(p => p.title && p.image);
}

function generateMockAiPosts() {
  return [
    {
      title: "Companion Planting Myths vs. Facts",
      excerpt: "What actually helps your veggies thrive, backed by horticulture studies.",
      image: "/media/ai-companion.jpg",
      category: "AI Insight",
      date: new Date().toDateString(),
    },
    {
      title: "Quick Guide: Bottom Watering Houseplants",
      excerpt: "A simple method to reduce gnats and overwatering.",
      image: "/media/ai-bottom-water.jpg",
      category: "AI Tip",
      date: new Date(Date.now() - 86400000).toDateString(),
    },
    {
      title: "Soil pH Cheat Sheet",
      excerpt: "Know your pH to match plants with the right medium.",
      image: "/media/ai-ph.jpg",
      category: "AI Guide",
      date: new Date(Date.now() - 2 * 86400000).toDateString(),
    },
  ];
}

export default function TonyasBloomsWireframe() {
  const [tonyaPosts] = useState(tonyaPostsSeed);
  const [aiPosts, setAiPosts] = useState([]);
  const [loadingAi, setLoadingAi] = useState(true);

  const [radioOn, setRadioOn] = useState(false);
  const playerRef = useRef(null);
  const apiReadyRef = useRef(false);
  const playerReadyRef = useRef(false);

  function ensureYouTubeAPI() {
    if (apiReadyRef.current) return Promise.resolve();
    return new Promise((resolve) => {
      if (window.YT && window.YT.Player) {
        apiReadyRef.current = true;
        resolve();
        return;
      }
      let existing = document.getElementById("ytapi");
      if (!existing) {
        const tag = document.createElement("script");
        tag.id = "ytapi";
        tag.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(tag);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        apiReadyRef.current = true;
        if (typeof prev === "function") prev();
        resolve();
      };
    });
  }

  function createPlayer() {
    if (!apiReadyRef.current || playerRef.current) return;
    // Lo-fi hip hop radio - beats to relax/study to
    playerRef.current = new window.YT.Player("radio-player", {
      height: "0",
      width: "0",
      videoId: "jfKfPfyJRdk",
      playerVars: {
        autoplay: 0,
        controls: 0,
        loop: 1,
        playlist: "jfKfPfyJRdk",
        origin: window.location.origin,
      },
      events: {
        onReady: () => {
          playerReadyRef.current = true;
          if (radioOn) {
            try { playerRef.current.playVideo(); } catch {}
          }
        },
      },
    });
  }

  useEffect(() => {
    let active = true;
    ensureYouTubeAPI().then(() => {
      if (!active) return;
      createPlayer();
      if (playerReadyRef.current && playerRef.current) {
        try {
          if (radioOn) playerRef.current.playVideo(); else playerRef.current.pauseVideo();
        } catch {}
      }
    });
    return () => { active = false; };
  }, [radioOn]);

  useEffect(() => {
    const vids = Array.from(document.querySelectorAll("video"));
    const onAnyPlay = () => {
      if (playerRef.current && playerReadyRef.current) {
        try { playerRef.current.pauseVideo(); } catch {}
      }
      setRadioOn(false);
    };
    vids.forEach(v => v.addEventListener("play", onAnyPlay));
    return () => vids.forEach(v => v.removeEventListener("play", onAnyPlay));
  }, []);

  const toggleRadio = () => setRadioOn(v => !v);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingAi(true);
      const urls = [
        "/api/ai-posts",
        "/media/ai-posts.json",
      ];
      let loaded = [];
      for (const u of urls) {
        try {
          const data = await safeFetchJson(u);
          loaded = normalizePosts(data);
          if (loaded.length) break;
        } catch (e) {
          // intentionally quiet; fallbacks below
        }
      }
      if (!loaded.length) loaded = normalizePosts(generateMockAiPosts());
      if (!cancelled) {
        setAiPosts(loaded);
        setLoadingAi(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const combinedPosts = useMemo(() => {
    const withStamp = (arr, source) => arr.map(p => ({ ...p, _ts: parseDateSafe(p.date)?.getTime() || 0, _src: source }));
    const merged = [...withStamp(tonyaPosts, "tonya"), ...withStamp(aiPosts, "ai")];
    const dedup = new Map();
    for (const p of merged) {
      const key = (p.title + "|" + p.date).toLowerCase();
      if (!dedup.has(key)) dedup.set(key, p);
    }
    return Array.from(dedup.values()).sort((a, b) => b._ts - a._ts);
  }, [tonyaPosts, aiPosts]);

  useEffect(() => {
    function selfTests() {
      console.assert(Array.isArray(combinedPosts), "combinedPosts should be an array");
      console.assert(combinedPosts.every(p => typeof p.title === "string" && p.title.length > 0), "each post needs a title");
      console.assert(combinedPosts.every(p => typeof p.image === "string" && p.image.length > 0), "each post needs an image");
      console.assert(combinedPosts.every(p => typeof p.category === "string"), "each post needs a category");
    }
    selfTests();
  }, [combinedPosts]);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-800">
      <motion.header
        variants={navVariants}
        initial="hidden"
        animate="visible"
        className="sticky top-0 z-40 backdrop-blur bg-white/70 border-b border-neutral-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-[#0F5132]/10 grid place-items-center text-[#0F5132] font-bold">TB</span>
            <div>
              <div className="text-lg font-semibold">Tonya's Blooms</div>
              <div className="text-xs text-neutral-500">Grow • Learn • Bloom</div>
            </div>
          </div>
            <button onClick={toggleRadio} aria-pressed={radioOn} aria-label="Toggle lofi radio" className="hidden sm:inline-flex items-center gap-1 ml-2 rounded-full border border-green-700 px-2 py-1 text-xs font-medium text-green-800 hover:bg-green-50">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M3 10v4a2 2 0 0 0 2 2h3l4 3V7l-4 3H5a2 2 0 0 0-2 2z"/><path d="M14 9.5a5.5 5.5 0 0 1 0 5"/></svg>
              <span>{radioOn ? "Radio On" : "Radio"}</span>
            </button>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="#home" className="hover:text-green-700">Home</a>
            <a href="#about" className="hover:text-green-700">About</a>
            <a href="#blog" className="hover:text-green-700">Blog</a>
            <a href="#corner" className="hover:text-green-700">Tonya's Corner</a>
            <a href="#community" className="hover:text-green-700">Community</a>
            <a href="#gallery" className="hover:text-green-700">Gallery</a>
          </nav>
          <a href="#newsletter" className="inline-flex items-center rounded-2xl border border-green-700 px-3 py-1.5 text-sm font-medium text-green-800 hover:bg-green-50">Join Newsletter</a>
        </div>
      </motion.header>

      <div id="radio-player-container" className="absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none -z-10"><div id="radio-player"></div></div>

      <section id="home" className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 items-center py-12">
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
              Welcome to <span className="text-green-700">Tonya's Blooms</span>
            </h1>
            <p className="mt-4 text-neutral-600 max-w-prose">
              Hi, I’m Tonya. This is my little corner of the internet where I share the joy of growing plants, learning from the seasons, and blooming together as a community.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#blog" className="rounded-2xl bg-green-700 text-white px-4 py-2 text-sm font-medium hover:bg-green-800">Read the Blog</a>
              <a href="#corner" className="rounded-2xl bg-white border border-green-700 text-green-800 px-4 py-2 text-sm font-medium hover:bg-green-50">Explore Tonya's Corner</a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {heroImages.map((img, i) => (
              <ImageCard key={img.src} src={img.src} alt={img.alt} index={i} variants={cardVariants} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white/70 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <h2 className="text-2xl font-bold">A quick hello from Tonya</h2>
            <p className="mt-2 text-neutral-600">Short clips to set the tone—why plants matter to me, and how we’ll grow together.</p>
          </div>
          <div className="grid gap-4">
            {videos.map((v) => (
              <div key={v.src} className="rounded-2xl overflow-hidden border border-neutral-200 bg-black/5">
                <video controls poster={v.poster} className="w-full h-full" preload="metadata">
                  <source src={v.src} type="video/mp4" />
                </video>
                <div className="px-4 py-2 text-sm text-neutral-600">{v.caption}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-3xl font-bold">About Tonya</h2>
            <p className="mt-3 text-neutral-700">I started gardening as a way to slow down and reconnect with nature. Over time, it became a passion I couldn’t help but share. Tonya's Blooms is where I document experiments, celebrate wins, and learn from setbacks—always with soil under my nails.</p>
            <div className="mt-6 flex gap-3">
              <a href="#community" className="rounded-2xl bg-green-700 text-white px-4 py-2 text-sm font-medium hover:bg-green-800">Join the Bloom Circle</a>
              <a href="#corner" className="rounded-2xl bg-white border border-green-700 text-green-800 px-4 py-2 text-sm font-medium hover:bg-green-50">Visit Tonya's Corner</a>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {aboutPhotos.map((img) => (
              <SquareCard key={img.src} src={img.src} alt={img.alt} />
            ))}
          </div>
        </div>
      </section>

      <section id="blog" className="bg-white border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <h2 className="text-3xl font-bold">From the Blog</h2>
              <p className="text-neutral-600 mt-1">Chronicles, tips, and seasonal notes from the garden and AI insights.</p>
            </div>
            <a href="#" className="text-green-800 hover:underline">View all posts →</a>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {(loadingAi ? tonyaPosts : combinedPosts).map((post, i) => (
              <motion.article
                key={`${post.title}-${post.date}-${i}`}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                custom={i}
                className="rounded-2xl overflow-hidden border border-neutral-200 bg-white shadow-sm"
              >
                <div className="aspect-[4/3] bg-neutral-200">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                </div>
                <div className="p-4">
                  <div className="text-xs uppercase tracking-wide text-green-700">{post.category}</div>
                  <h3 className="mt-1 font-semibold">{post.title}</h3>
                  <p className="mt-1 text-sm text-neutral-600">{post.excerpt}</p>
                  <div className="mt-3 text-xs text-neutral-500">{post.date}</div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section id="corner" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="text-3xl font-bold">Tonya's Corner</h2>
            <p className="text-neutral-600 mt-1">A cozy hub of guides, checklists, and tutorials.</p>
          </div>
          <a href="#" className="text-green-800 hover:underline">Browse the library →</a>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cornerGuides.map((g) => (
            <div key={g.title} className="rounded-2xl overflow-hidden border border-neutral-200 bg-white">
              <div className="aspect-[4/3] bg-neutral-200">
                <img src={g.image} alt={g.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>
              <div className="p-4">
                <h3 className="font-semibold">{g.title}</h3>
                <p className="text-sm text-neutral-600 mt-1">Quick-start guide with checklists & FAQs.</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="community" className="bg-green-50 border-y border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold">Join the Bloom Circle</h2>
            <p className="mt-2 text-neutral-700">A gentle space for plant lovers—ask questions, share your progress, and cheer on fellow growers. Start with the newsletter, then hop into the forum.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#newsletter" className="rounded-2xl bg-green-700 text-white px-4 py-2 text-sm font-medium hover:bg-green-800">Subscribe to Newsletter</a>
              <a href="#forum" className="rounded-2xl bg-white border border-green-700 text-green-800 px-4 py-2 text-sm font-medium hover:bg-green-50">Visit Forum</a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {gallery.slice(0, 4).map((src, i) => (
              <SquareCard key={`${src}-${i}`} src={src} alt={`Community highlight ${i + 1}`} />
            ))}
          </div>
        </div>
      </section>

      <section id="gallery" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="text-3xl font-bold">Gallery</h2>
            <p className="text-neutral-600 mt-1">A peek at recent blooms and leafy moments.</p>
          </div>
          <a href="#" className="text-green-800 hover:underline">Submit your photo →</a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {gallery.map((src, i) => (
            <SquareCard key={`${src}-g-${i}`} src={src} alt={`Gallery image ${i + 1}`} />
          ))}
        </div>
      </section>

      <section id="newsletter" className="bg-white border-t border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h2 className="text-3xl font-bold">Subscribe to The Bloom Letter</h2>
          <p className="mt-2 text-neutral-600">Weekly tips, seasonal checklists, and community highlights—straight to your inbox.</p>
          <form
  action="/subscribe.php"
  method="POST"
  className="mt-6 grid sm:grid-cols-[1fr_auto] gap-3 max-w-xl mx-auto"
>
  <input
    type="email"
    name="email"
    placeholder="you@example.com"
    required
    className="rounded-2xl border border-neutral-300 px-4 py-3 outline-none focus:ring-2 focus:ring-green-600"
  />
  <button
    type="submit"
    className="rounded-2xl bg-green-700 text-white px-6 py-3 font-medium hover:bg-green-800"
  >
    Join
  </button>
</form>

          <p className="text-xs text-neutral-500 mt-3">We respect your privacy. Unsubscribe anytime.</p>
        </div>
      </section>

      <footer className="bg-neutral-900 text-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-4 gap-8">
          <div>
            <div className="text-lg font-semibold">Tonya's Blooms</div>
            <p className="text-sm text-neutral-400 mt-2">© {new Date().getFullYear()} Tonya's Blooms. All rights reserved.</p>
          </div>
          <div>
            <div className="font-semibold mb-2">Explore</div>
            <ul className="space-y-1 text-sm text-neutral-400">
              <li><a href="#blog" className="hover:text-white">Blog</a></li>
              <li><a href="#corner" className="hover:text-white">Tonya's Corner</a></li>
              <li><a href="#community" className="hover:text-white">Community</a></li>
              <li><a href="#gallery" className="hover:text-white">Gallery</a></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-2">Community</div>
            <ul className="space-y-1 text-sm text-neutral-400">
              <li><a href="#forum" className="hover:text-white">Forum</a></li>
              <li><a href="#newsletter" className="hover:text-white">Newsletter</a></li>
              <li><a href="#" className="hover:text-white">Instagram</a></li>
              <li><a href="#" className="hover:text-white">TikTok</a></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-2">Contact</div>
            <ul className="space-y-1 text-sm text-neutral-400">
              <li>hello@tonyasblooms.com</li>
              <li>Florida, USA</li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
